import { useState } from "react";
import api from "@/lib/api";
import { Flag } from "lucide-react";
import { toast } from "sonner";

export const REASON_OPTIONS = [
    "Spam or scam",
    "Inappropriate / offensive",
    "Incorrect or misleading info",
    "Cancelled / no longer in business",
    "Duplicate listing",
    "Harassment or abuse",
];

// Human-readable label for what's being reported (used in the modal heading).
const READABLE = {
    post: "post",
    reply: "comment",
    team: "team listing",
    event: "event listing",
    trainer: "trainer",
    trainer_listing: "trainer listing",
    review: "review",
    circuit: "circuit",
};

/**
 * Shared report/flag modal. Reasons are selectable buttons plus a free-text box.
 * Submits the combined reason to /flags with the given target so admins see
 * exactly where it was flagged and get a link straight to it.
 */
export default function ReportModal({ open, onClose, targetType, targetId }) {
    const [selected, setSelected] = useState([]);
    const [note, setNote] = useState("");
    const [busy, setBusy] = useState(false);
    if (!open) return null;

    const readable = READABLE[targetType] || (targetType || "item").replace(/_/g, " ");
    const toggle = (r) => setSelected((s) => (s.includes(r) ? s.filter((x) => x !== r) : [...s, r]));
    const reasonText = [...selected, ...(note.trim() ? [`Note: ${note.trim()}`] : [])].join("; ");
    const canSubmit = reasonText.length > 0;

    const close = () => { setSelected([]); setNote(""); onClose?.(); };

    const submit = async () => {
        if (!canSubmit || busy) return;
        setBusy(true);
        try {
            await api.post("/flags", { target_type: targetType, target_id: targetId, reason: reasonText.slice(0, 500) });
            toast.success("Reported to admins for review");
            close();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not report");
        } finally { setBusy(false); }
    };

    return (
        <div className="fixed inset-0 z-[1300] bg-black/80 flex items-center justify-center p-4" data-testid="report-modal" onClick={close}>
            <div className="w-full max-w-md bg-[#141414] border border-[#2a2a2a] p-5" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center gap-2 text-red-400 mb-1">
                    <Flag className="h-4 w-4" />
                    <span className="text-[10px] uppercase tracking-[0.2em]">Report this {readable}</span>
                </div>
                <p className="text-xs text-[#a0a0a0] mb-4">Pick one or more reasons. Admins get a link straight to this {readable}.</p>

                <div className="flex flex-wrap gap-2" data-testid="report-reasons">
                    {REASON_OPTIONS.map((r) => {
                        const on = selected.includes(r);
                        return (
                            <button
                                key={r}
                                type="button"
                                data-testid={`report-reason-${r.split(" ")[0].toLowerCase()}`}
                                onClick={() => toggle(r)}
                                className={`px-3 py-1.5 text-xs border transition-colors ${on ? "bg-red-600 border-red-600 text-white" : "bg-[#0f0f0f] border-[#2a2a2a] text-[#c9c9c9] hover:border-red-500 hover:text-red-400"}`}
                            >
                                {r}
                            </button>
                        );
                    })}
                </div>

                <div className="mt-4">
                    <label className="block text-[10px] uppercase tracking-[0.2em] text-[#a0a0a0] mb-1.5">Add details (optional)</label>
                    <textarea
                        data-testid="report-note"
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        rows={3}
                        maxLength={400}
                        placeholder="Tell us what's wrong…"
                        className="w-full text-sm bg-[#0f0f0f] border border-[#2a2a2a] p-2.5 text-white rounded-none focus:outline-none focus:border-red-500"
                    />
                </div>

                <div className="flex justify-end gap-2 mt-4">
                    <button type="button" onClick={close} className="h-9 px-3 text-[10px] uppercase tracking-[0.2em] text-[#a0a0a0] hover:text-white">Cancel</button>
                    <button type="button" data-testid="report-submit" onClick={submit} disabled={busy || !canSubmit} className="h-9 px-4 bg-red-600 hover:bg-red-500 text-white text-[10px] uppercase tracking-[0.2em] font-semibold disabled:opacity-50">
                        {busy ? "Reporting…" : "Report"}
                    </button>
                </div>
            </div>
        </div>
    );
}
